import { useState, useEffect } from "react";

export default function IowaHeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-sm border-b border-gray-100" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-[70px]">
          <a href="https://fastpassportcenter.com" className="flex items-center">
            <img
              src="https://staging.fastpassportcenter.com/wp-content/uploads/2026/02/Header.svg"
              alt="Fast Passport Center"
              className={`h-9 w-auto transition-all ${scrolled ? "" : "brightness-0 invert"}`}
            />
          </a>
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: "Cities", href: "#cities" },
              { label: "Resources", href: "#resources" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <a
              href="tel:8772530084"
              className={`hidden md:inline-flex items-center gap-1.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"
              }`}
            >
              <i className="ri-phone-line"></i>877-253-0084
            </a>
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] text-sm font-bold px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
            >
              Get Started
            </a>
            <button
              className="md:hidden flex items-center justify-center w-9 h-9"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl ${scrolled ? "text-[#1A365D]" : "text-white"}`}></i>
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-3">
            {[
              { label: "Cities", href: "#cities" },
              { label: "Resources", href: "#resources" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="block text-sm font-medium text-[#1A365D] hover:text-[#F6C344] py-1"
              >
                {item.label}
              </a>
            ))}
            <a href="tel:8772530084" className="block text-sm font-semibold text-[#1A365D] py-1">
              <i className="ri-phone-line mr-1.5"></i>877-253-0084
            </a>
          </div>
        )}
      </nav>

      <section className="relative min-h-[580px] md:min-h-[680px] flex items-center justify-center overflow-hidden bg-[#0F2240]">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20golden%20Iowa%20cornfields%20stretching%20to%20the%20horizon%20at%20golden%20hour%2C%20rolling%20green%20hills%2C%20a%20winding%20country%20road%2C%20wind%20turbines%20in%20the%20distance%2C%20soft%20amber%20and%20warm%20brown%20tones%20blending%20with%20deep%20blue%20sky%2C%20minimal%20composition%20with%20lots%20of%20negative%20space%20in%20the%20upper%20portion%20for%20text%20overlay%2C%20photorealistic%20landscape%20photography%20style&width=1920&height=800&seq=iowa-hero-001&orientation=landscape"
            alt="Iowa countryside"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F2240]/80 via-[#0F2240]/70 to-[#0F2240]/90"></div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-16 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-map-pin-2-line text-[#F6C344] text-sm"></i>
            <span className="text-white/80 text-sm font-medium">Serving All of Iowa</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Expedited Passport Services<br className="hidden md:block" /> Across Iowa
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            From Des Moines to the Quad Cities, Fast Passport Center connects Iowa travelers with emergency and expedited passport processing — as fast as 24 hours.
          </p>
          <a
            href="#cities"
            className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap"
          >
            Find Your City <i className="ri-arrow-down-line"></i>
          </a>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-5 md:gap-8">
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <div className="flex items-center gap-0.5">
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
              </div>
              <span className="font-semibold">4.9 / 5</span>
            </div>
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <i className="ri-award-line text-[#F6C344] text-base"></i>
              <span>A+ BBB Rating</span>
            </div>
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <i className="ri-calendar-check-line text-[#F6C344] text-base"></i>
              <span>20+ Years</span>
            </div>
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <i className="ri-shield-check-line text-[#F6C344] text-base"></i>
              <span>Registered State Dept. Courier</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}